/**
 * First-touch capture of the user's entry channel + UTMs.
 *
 * Card n447gxvy — OBS F1.1 trackEvent helper + commonProps + entryChannel.
 * Card STNFE3gt — Reconciliar vocabulario FE con BE `ALLOWED_ENTRY_CHANNELS`.
 *
 * Contract
 * --------
 * - Captura ONCE en el primer landing. Nunca sobreescribe. Un `localStorage`
 *   con `inbest.entry_channel.v1` es la fuente de verdad para el resto de
 *   la sesión del navegador.
 * - `detectEntryChannel` es una función pura sobre `(referrer, utm_medium,
 *   utm_source)`. La prioridad del árbol de decisión es (más específica
 *   primero → fallback):
 *     1. `paid`      — utm_medium ∈ {cpc, ppc, paid, ads, paid_search} OR
 *                       utm_source contiene ads/adwords/google-ads/facebook-ads.
 *                       (Nomenclatura vieja: `paid_ads` — remap por STNFE3gt.)
 *     2. `display`   — utm_medium=display.
 *     3. `video`     — utm_medium=video OR referrer YouTube/Vimeo/TikTok.
 *     4. `affiliate` — utm_medium=affiliate OR utm_source contiene affiliate.
 *     5. `social`    — referrer/utm_source con whatsapp/wa.me/facebook/
 *                       instagram/twitter/x/linkedin/tiktok.
 *                       (Nomenclatura vieja: `whatsapp` — remap por STNFE3gt.)
 *     6. `email`     — utm_medium=email OR utm_source=newsletter OR
 *                       referrer webmail (mail.google, outlook, yahoo.mail).
 *     7. `organic`   — referrer buscador (google/bing/duckduckgo/yahoo).
 *                       (Nomenclatura vieja: `search` — remap por STNFE3gt.)
 *     8. `referral`  — cualquier referrer externo no-inbest sin match previo.
 *     9. `direct`    — sin referrer + sin utm, o referrer del mismo dominio.
 *    10. `unknown`   — fallback: hay señal pero no la pudimos clasificar.
 * - Pura y SSR-safe: cada acceso a `window` / `document` / `localStorage`
 *   está gated por `typeof window !== 'undefined'`.
 */

/**
 * Vocabulario canónico. **Debe matchear byte-a-byte** con el BE
 * `ALLOWED_ENTRY_CHANNELS` en
 * `services/projects-service/shared/constants.py` (card W5cfCuyt +
 * migración 0060). El invariante se guarda con
 * `entryChannel-consistency.test.ts` — cualquier drift rompe CI.
 *
 * Orden alfabético para diffs estables (mismo criterio que la constante
 * Python). `unknown` es el bucket de escape para tráfico no clasificable
 * y NO se debe remover — es el fallback runtime del detector.
 */
export const ENTRY_CHANNELS = [
  'affiliate',
  'direct',
  'display',
  'email',
  'organic',
  'paid',
  'referral',
  'social',
  'unknown',
  'video',
] as const;

/** Canonical vocabulary type derived from the runtime array. */
export type EntryChannel = (typeof ENTRY_CHANNELS)[number];

/**
 * Registro persistido al primer landing. TODOS los campos son inmutables
 * una vez escritos — no re-computar aunque el usuario navegue.
 */
export interface FirstTouch {
  entry_channel: EntryChannel;
  first_utm_source?: string;
  first_utm_medium?: string;
  first_utm_campaign?: string;
  first_utm_term?: string;
  first_utm_content?: string;
  first_referrer?: string;
  first_landing_path?: string;
  /** ISO-8601 UTC del momento de captura. */
  captured_at: string;
}

/** localStorage key. La versión sufija permite migraciones futuras. */
const STORAGE_KEY = 'inbest.entry_channel.v1';

/**
 * Clasifica el canal de entrada a partir del referrer y UTMs. Función
 * pura — no toca `window` ni `document`. Testeable con inputs sintéticos.
 *
 * El vocabulario devuelto matchea `ENTRY_CHANNELS` (fuente única BE).
 * Si un input no matchea ninguna rama pero tiene señal (referrer/utm
 * no vacío), retorna `'unknown'` en lugar de degradar a `'direct'`.
 */
export function detectEntryChannel(params: {
  referrer: string;
  utm_medium?: string;
  utm_source?: string;
}): EntryChannel {
  const referrer = params.referrer || '';
  const utmMedium = (params.utm_medium || '').toLowerCase();
  const utmSource = (params.utm_source || '').toLowerCase();

  // 1. paid — señales de campañas pagas dominan sobre todo lo demás (el
  //    referrer puede ser google.com para un ad de Google Ads).
  //    Remap STNFE3gt: `paid_ads` → `paid` (vocabulario BE).
  const paidMediums = new Set(['cpc', 'ppc', 'paid', 'ads', 'paid_search']);
  if (utmMedium && paidMediums.has(utmMedium)) return 'paid';
  if (utmSource && /ads|adwords|google-ads|facebook-ads/.test(utmSource)) {
    return 'paid';
  }

  // 2. display — banners no clasificados como paid_search.
  if (utmMedium === 'display') return 'display';

  // 3. video — plataformas de video orgánicas (YouTube/Vimeo/TikTok) o
  //    campaña marcada como video.
  if (utmMedium === 'video') return 'video';
  if (referrer && /youtube\.com|youtu\.be|vimeo\.com|tiktok\.com/i.test(referrer)) {
    return 'video';
  }

  // 4. affiliate — programa de afiliados.
  if (utmMedium === 'affiliate') return 'affiliate';
  if (utmSource && /affiliate/.test(utmSource)) return 'affiliate';

  // 5. social — WhatsApp + redes sociales orgánicas. Referrer O utm_source.
  //    Remap STNFE3gt: `whatsapp` → `social` (vocabulario BE).
  const socialReferrerRe =
    /whatsapp|wa\.me|api\.whatsapp|facebook\.com|instagram\.com|twitter\.com|x\.com|linkedin\.com|t\.co/i;
  const socialUtmRe = /whatsapp|facebook|instagram|twitter|linkedin|tiktok/;
  if (referrer && socialReferrerRe.test(referrer)) return 'social';
  if (utmSource && socialUtmRe.test(utmSource)) return 'social';

  // 6. email — utm_medium=email, utm_source=newsletter, o referrer webmail.
  if (utmMedium === 'email') return 'email';
  if (utmSource === 'newsletter') return 'email';
  if (referrer && /mail\.google|outlook|yahoo\.mail/i.test(referrer)) {
    return 'email';
  }

  // 7. organic — referrer de motor de búsqueda orgánico.
  //    Remap STNFE3gt: `search` → `organic` (vocabulario BE).
  if (referrer && /google\.com|bing\.com|duckduckgo|yahoo\.com/i.test(referrer)) {
    return 'organic';
  }

  // 8. referral — cualquier referrer externo no-inbest.
  if (referrer && !/inbest/i.test(referrer)) return 'referral';

  // 9. direct — sin referrer y sin utm, o referrer del mismo dominio.
  if (!referrer && !utmMedium && !utmSource) return 'direct';
  if (referrer && /inbest/i.test(referrer)) return 'direct';

  // 10. unknown — fallback defensivo: hay señal pero no la pudimos clasificar.
  return 'unknown';
}

/**
 * Lee el registro persistido de first-touch. Retorna `null` cuando:
 *   - `localStorage` no está disponible (SSR, private mode, disabled).
 *   - Todavía no se corrió `captureFirstTouchIfNew`.
 *   - El payload es corrupto (JSON inválido).
 */
export function getFirstTouch(): FirstTouch | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FirstTouch;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Header que chat-service lee para atribuir el lead (card xHojZcrU). Mismo
 * nombre que `ENTRY_CHANNEL_HEADER` en
 * `services/chat-service/app/v2/services/lead_entry_channel.py`.
 *
 * Por qué header y no body hacia chat-service: los requests de los forms del
 * Perfilador (`/wizard/lead-capture`, `/wizard/high-intent`) son
 * `extra="forbid"`. Si este FE se despliega antes que chat-service, un campo
 * nuevo en el body daría 422 y el lead se perdería; un header desconocido se
 * ignora.
 */
export const ENTRY_CHANNEL_HEADER = 'X-Entry-Channel';

/**
 * Card xHojZcrU — el canal first-touch para adjuntar a una request de
 * captura de lead, o `undefined`.
 *
 * Sólo devuelve valores del vocabulario canónico: `localStorage` lo puede
 * editar cualquiera (o una versión vieja del FE pudo dejar un label
 * retirado), y lo que viaja con un lead no puede ser la razón de que se
 * pierda. Fuera de vocabulario → no se manda, y el backend cae a `unknown`.
 */
export function getEntryChannelForCapture(): EntryChannel | undefined {
  const channel: unknown = getFirstTouch()?.entry_channel;
  if (typeof channel !== 'string') return undefined;
  return (ENTRY_CHANNELS as readonly string[]).includes(channel)
    ? (channel as EntryChannel)
    : undefined;
}

/**
 * Headers para un request a chat-service que puede terminar en un lead.
 * Vacío cuando no hay canal usable (nunca manda un header en blanco).
 */
export function entryChannelHeaders(): Record<string, string> {
  const channel = getEntryChannelForCapture();
  return channel ? { [ENTRY_CHANNEL_HEADER]: channel } : {};
}

/**
 * Persiste el registro de first-touch si NO existe ya. Idempotente y
 * seguro de llamar múltiples veces (el segundo llamado es no-op). Debe
 * invocarse desde `App.tsx` en `useEffect` al mount inicial.
 *
 * Silent no-op cuando `window`/`localStorage` no están disponibles
 * (SSR, Safari private mode con quota=0, etc.). El evento downstream se
 * degrada a "sin entry_channel adjunto" — nunca falla.
 */
export function captureFirstTouchIfNew(): void {
  if (typeof window === 'undefined') return;
  // Idempotencia: si ya hay un registro, no escribir de nuevo.
  if (getFirstTouch()) return;

  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || '';
  const utmSource = params.get('utm_source') || undefined;
  const utmMedium = params.get('utm_medium') || undefined;

  const record: FirstTouch = {
    entry_channel: detectEntryChannel({
      referrer,
      utm_medium: utmMedium,
      utm_source: utmSource,
    }),
    first_utm_source: utmSource,
    first_utm_medium: utmMedium,
    first_utm_campaign: params.get('utm_campaign') || undefined,
    first_utm_term: params.get('utm_term') || undefined,
    first_utm_content: params.get('utm_content') || undefined,
    first_referrer: referrer || undefined,
    first_landing_path: window.location.pathname,
    captured_at: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Quota, SecurityError, private mode → degradar silenciosamente. El
    // evento se emitirá sin first-touch adjunto.
  }
}

/**
 * Test-only helper — borrar el registro para escenarios de "primer
 * landing" en unit tests. NO usar desde código de producto.
 */
export function __resetFirstTouchForTests(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* swallow */
  }
}
